/**
 * Workflow Builder - Admin UI for creating and managing approval workflows
 */

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Plus, 
  Settings, 
  Trash2, 
  ChevronRight,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  Copy,
  MoreVertical,
  Filter,
  Search,
  Workflow,
  GitBranch,
  Shield,
  UserCheck,
  Building2,
  MapPin,
  Layers,
  Calendar,
  UserCog,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfirmDialog } from "@/components/ui/confirm-dialog";
import { useErrorDialog } from "@/components/ui/error-dialog";
import { FioriPageHeader } from "@/components/fiori";
import { ApproverConfigModal } from "./ApproverConfigModal";
import { EscalationRulesModal } from "./EscalationRulesModal";
import { WorkflowSimulator } from "./WorkflowSimulator";

// Static value arrays (labels resolved inside component via t())
const PROCESS_TYPE_VALUES = ["admin_visit","work_permit","material_entry","tep","mop","escort","mcm","tdp","mhv"];
const STAGE_TYPE_VALUES = ["individual","role","approval_role","group","group_role","group_hierarchy","dynamic_field","manager","external_manager","site_manager","zone_owner","custom_resolver"];
const APPROVAL_MODE_VALUES = ["any","all","percentage"];
const APPROVER_TYPE_VALUES = ["user","role","approval_role","group","group_role","hierarchy_level","dynamic_field","manager_chain"];
const CONDITION_OPERATOR_VALUES = [
  { value: "equals", types: ["all"] },
  { value: "not_equals", types: ["all"] },
  { value: "in", types: ["select", "multi"] },
  { value: "not_in", types: ["select", "multi"] },
  { value: "greater_than", types: ["number"] },
  { value: "less_than", types: ["number"] },
  { value: "between", types: ["number", "time"] },
  { value: "contains", types: ["text"] },
  { value: "starts_with", types: ["text"] },
  { value: "is_null", types: ["all"] },
  { value: "is_not_null", types: ["all"] },
];

export function WorkflowBuilder() {
  const { t } = useTranslation();
  const [selectedWorkflow, setSelectedWorkflow] = useState<number | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isStageDialogOpen, setIsStageDialogOpen] = useState(false);
  const [isEditStageDialogOpen, setIsEditStageDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<any>(null);
  const [isConditionDialogOpen, setIsConditionDialogOpen] = useState(false);
  const [isApproverConfigOpen, setIsApproverConfigOpen] = useState(false);
  const [editingApprover, setEditingApprover] = useState<any>(null);
  const [selectedStageForApprovers, setSelectedStageForApprovers] = useState<number | null>(null);
  const [isEscalationRulesOpen, setIsEscalationRulesOpen] = useState(false);
  const [selectedStageForEscalation, setSelectedStageForEscalation] = useState<number | null>(null);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProcessType, setFilterProcessType] = useState<string | null>(null);

  // Queries
  const { data: workflows, isLoading, refetch } = trpc.workflows.list.useQuery({
    includeInactive: true,
  });

  const { data: workflowDetails, refetch: refetchDetails } = trpc.workflows.getDetails.useQuery(
    { workflowId: selectedWorkflow! },
    { enabled: !!selectedWorkflow }
  );

  const { data: approvalRoles } = trpc.workflows.listApprovalRoles.useQuery();
  
  // Fetch data for condition value dropdowns
  const { data: sites } = trpc.sites.getAll.useQuery();
  const { data: zones } = trpc.zones.getAll.useQuery();
  const { data: areas } = trpc.areas.getAll.useQuery();
  const { data: groups } = trpc.groups.list.useQuery();
  const { data: departments } = trpc.departments.list.useQuery();
  const { data: roles } = trpc.roles.list.useQuery();
  const { data: users } = trpc.users.list.useQuery();
  // Mutations
  const createWorkflow = trpc.workflows.create.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.workflowCreated', 'Workflow created successfully'));
      setIsCreateDialogOpen(false);
      refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateWorkflow = trpc.workflows.update.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.workflowUpdated', 'Workflow updated successfully'));
      refetch();
      refetchDetails();
    },
    onError: (error) => toast.error(error.message),
  });

  // Confirmation and error dialogs
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { showError, ErrorDialogComponent } = useErrorDialog();

  const deleteWorkflow = trpc.workflows.delete.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.workflowDeleted', 'Workflow deleted successfully'));
      setSelectedWorkflow(null);
      refetch();
    },
    onError: (error) => showError(error.message || t('workflows.deleteWorkflowFailed', 'Failed to delete workflow'), t('common.deleteFailed', 'Delete Failed')),
  });

  const addStage = trpc.workflows.addStage.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.stageAdded', 'Stage added successfully'));
      setIsStageDialogOpen(false);
      refetchDetails();
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteStage = trpc.workflows.deleteStage.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.stageDeleted', 'Stage deleted successfully'));
      refetchDetails();
    },
    onError: (error) => showError(error.message || t('workflows.deleteStageFailed', 'Failed to delete stage'), t('common.deleteFailed', 'Delete Failed')),
  });

  const updateStage = trpc.workflows.updateStage.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.stageUpdated', 'Stage updated successfully'));
      setIsEditStageDialogOpen(false);
      setEditingStage(null);
      refetchDetails();
    },
    onError: (error) => toast.error(error.message),
  });

  const addCondition = trpc.workflows.addCondition.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.conditionAdded', 'Condition added successfully'));
      setIsConditionDialogOpen(false);
      refetchDetails();
      // Reset form
      setNewCondition({
        conditionType: "",
        conditionOperator: "equals",
        conditionValue: "",
        logicalGroup: 0,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const removeCondition = trpc.workflows.removeCondition.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.conditionRemoved', 'Condition removed successfully'));
      refetchDetails();
    },
    onError: (error) => showError(error.message || t('workflows.removeConditionFailed', 'Failed to remove condition'), t('common.deleteFailed', 'Delete Failed')),
  });

  const addApprover = trpc.workflows.addApprover.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.approverAdded', 'Approver added successfully'));
      setIsApproverConfigOpen(false);
      refetchDetails();
      setEditingApprover(null);
      setSelectedStageForApprovers(null);
    },
    onError: (error: any) => toast.error(error.message),
  });

  const removeApprover = trpc.workflows.removeApprover.useMutation({
    onSuccess: () => {
      toast.success(t('workflows.approverRemoved', 'Approver removed successfully'));
      refetchDetails();
    },
    onError: (error: any) => showError(error.message || t('workflows.removeApproverFailed', 'Failed to remove approver'), t('common.deleteFailed', 'Delete Failed')),
  });

  // Translated option arrays (useMemo so they re-compute on language change)
  const PROCESS_TYPES = useMemo(() => [
    { value: "admin_visit", label: t("requestTypes.admin_visit", "Admin Visit") },
    { value: "work_permit", label: t("requestTypes.work_permit", "Work Permit") },
    { value: "material_entry", label: t("requestTypes.material_entry", "Material Entry") },
    { value: "tep", label: t("requestTypes.tep", "TEP (Temporary Entry Pass)") },
    { value: "mop", label: t("requestTypes.mop", "MOP (Method of Procedure)") },
    { value: "escort", label: t("requestTypes.escort", "Escort Request") },
    { value: "mcm", label: t("requestTypes.mcm", "MCM (Managed Change Management)") },
    { value: "tdp", label: t("requestTypes.tdp", "TDP (Technical Data Package)") },
    { value: "mhv", label: t("requestTypes.mhv", "MHV (Material Handling Vehicle)") },
  ], [t]);

  const CONDITION_TYPES = useMemo(() => [
    { value: "site_id", label: t("workflow.conditionType.site_id", "Site"), category: t("workflow.category.location", "Location"), icon: Building2 },
    { value: "zone_id", label: t("workflow.conditionType.zone_id", "Zone"), category: t("workflow.category.location", "Location"), icon: MapPin },
    { value: "area_id", label: t("workflow.conditionType.area_id", "Area"), category: t("workflow.category.location", "Location"), icon: Layers },
    { value: "region", label: t("workflow.conditionType.region", "Region"), category: t("workflow.category.location", "Location"), icon: MapPin },
    { value: "process_type", label: t("workflow.conditionType.process_type", "Process Type"), category: t("workflow.category.request", "Request"), icon: Workflow },
    { value: "category", label: t("workflow.conditionType.category", "Category"), category: t("workflow.category.request", "Request"), icon: Filter },
    { value: "sub_category", label: t("workflow.conditionType.sub_category", "Sub-Category"), category: t("workflow.category.request", "Request"), icon: Filter },
    { value: "activity_risk", label: t("workflow.conditionType.activity_risk", "Activity Risk Level"), category: t("workflow.category.request", "Request"), icon: AlertTriangle },
    { value: "visitor_count", label: t("workflow.conditionType.visitor_count", "Visitor Count"), category: t("workflow.category.request", "Request"), icon: Users },
    { value: "request_duration", label: t("workflow.conditionType.request_duration", "Request Duration (hours)"), category: t("workflow.category.request", "Request"), icon: Clock },
    { value: "vip_visit", label: t("workflow.conditionType.vip_visit", "VIP Visit"), category: t("workflow.category.request", "Request"), icon: Shield },
    { value: "escort_required", label: t("workflow.conditionType.escort_required", "Escort Required"), category: t("workflow.category.request", "Request"), icon: UserCheck },
    { value: "access_level", label: t("workflow.conditionType.access_level", "Access Level"), category: t("workflow.category.request", "Request"), icon: Shield },
    { value: "requester_group", label: t("workflow.conditionType.requester_group", "Requester Group"), category: t("workflow.category.requester", "Requester"), icon: Users },
    { value: "requester_type", label: t("workflow.conditionType.requester_type", "Requester Type"), category: t("workflow.category.requester", "Requester"), icon: UserCog },
    { value: "requester_department", label: t("workflow.conditionType.requester_department", "Requester Department"), category: t("workflow.category.requester", "Requester"), icon: Building2 },
    { value: "requester_role", label: t("workflow.conditionType.requester_role", "Requester Role"), category: t("workflow.category.requester", "Requester"), icon: UserCog },
    { value: "has_mop", label: t("workflow.conditionType.has_mop", "Has MOP"), category: t("workflow.category.special", "Special"), icon: CheckCircle },
    { value: "has_mhv", label: t("workflow.conditionType.has_mhv", "Has MHV"), category: t("workflow.category.special", "Special"), icon: CheckCircle },
    { value: "time_range", label: t("workflow.conditionType.time_range", "Time Range"), category: t("workflow.category.time", "Time"), icon: Clock },
    { value: "working_hours", label: t("workflow.conditionType.working_hours", "Working Hours"), category: t("workflow.category.time", "Time"), icon: Clock },
    { value: "day_of_week", label: t("workflow.conditionType.day_of_week", "Day of Week"), category: t("workflow.category.time", "Time"), icon: Calendar },
  ], [t]);

  const CONDITION_OPERATORS = useMemo(() => CONDITION_OPERATOR_VALUES.map(op => ({
    ...op,
    label: t(`workflow.operator.${op.value}`, op.value.replace(/_/g, " ")),
  })), [t]);

  const DAYS_OF_WEEK = useMemo(() => [
    { value: "0", label: t("workflow.day.sunday", "Sunday") },
    { value: "1", label: t("workflow.day.monday", "Monday") },
    { value: "2", label: t("workflow.day.tuesday", "Tuesday") },
    { value: "3", label: t("workflow.day.wednesday", "Wednesday") },
    { value: "4", label: t("workflow.day.thursday", "Thursday") },
    { value: "5", label: t("workflow.day.friday", "Friday") },
    { value: "6", label: t("workflow.day.saturday", "Saturday") },
  ], [t]);

  const RISK_LEVELS = useMemo(() => [
    { value: "low", label: t("common.low", "Low") },
    { value: "medium", label: t("common.medium", "Medium") },
    { value: "high", label: t("common.high", "High") },
    { value: "critical", label: t("common.critical", "Critical") },
  ], [t]);

  const REQUESTER_TYPES = useMemo(() => [
    { value: "internal", label: t("workflow.requesterType.internal", "Internal Employee") },
    { value: "external", label: t("workflow.requesterType.external", "External Visitor") },
    { value: "contractor", label: t("workflow.requesterType.contractor", "Contractor") },
    { value: "vendor", label: t("workflow.requesterType.vendor", "Vendor") },
  ], [t]);

  const ACCESS_LEVELS = useMemo(() => [
    { value: "1", label: t("workflow.accessLevel.1", "Level 1 - Public Areas") },
    { value: "2", label: t("workflow.accessLevel.2", "Level 2 - Restricted Areas") },
    { value: "3", label: t("workflow.accessLevel.3", "Level 3 - Secure Areas") },
    { value: "4", label: t("workflow.accessLevel.4", "Level 4 - High Security") },
    { value: "5", label: t("workflow.accessLevel.5", "Level 5 - Critical Infrastructure") },
  ], [t]);

  const STAGE_TYPES = useMemo(() => [
    { value: "individual", label: t("workflow.stageType.individual", "Individual User") },
    { value: "role", label: t("workflow.stageType.role", "System Role") },
    { value: "approval_role", label: t("workflow.stageType.approval_role", "Approval Role") },
    { value: "group", label: t("workflow.stageType.group", "Group Members") },
    { value: "group_role", label: t("workflow.stageType.group_role", "Group Role") },
    { value: "group_hierarchy", label: t("workflow.stageType.group_hierarchy", "Group Hierarchy") },
    { value: "dynamic_field", label: t("workflow.stageType.dynamic_field", "Dynamic Field (e.g., Host)") },
    { value: "manager", label: t("workflow.stageType.manager", "Manager") },
    { value: "external_manager", label: t("workflow.stageType.external_manager", "External Manager") },
    { value: "site_manager", label: t("workflow.stageType.site_manager", "Site Manager") },
    { value: "zone_owner", label: t("workflow.stageType.zone_owner", "Zone Owner") },
    { value: "custom_resolver", label: t("workflow.stageType.custom_resolver", "Custom Resolver") },
  ], [t]);

  const APPROVAL_MODES = useMemo(() => [
    { value: "any", label: t("workflow.approvalMode.any", "Any (Single approval)") },
    { value: "all", label: t("workflow.approvalMode.all", "All (Everyone must approve)") },
    { value: "percentage", label: t("workflow.approvalMode.percentage", "Percentage (% must approve)") },
  ], [t]);

  const APPROVER_TYPES = useMemo(() => [
    { value: "user", label: t("workflow.approverType.user", "Specific User") },
    { value: "role", label: t("workflow.approverType.role", "System Role") },
    { value: "approval_role", label: t("workflow.approverType.approval_role", "Approval Role") },
    { value: "group", label: t("workflow.approverType.group", "User Group") },
    { value: "group_role", label: t("workflow.approverType.group_role", "Group Role") },
    { value: "hierarchy_level", label: t("workflow.approverType.hierarchy_level", "Hierarchy Level") },
    { value: "dynamic_field", label: t("workflow.approverType.dynamic_field", "Dynamic Field") },
    { value: "manager_chain", label: t("workflow.approverType.manager_chain", "Manager Chain") },
  ], [t]);

  // Handle delete workflow with confirmation
  const handleDeleteWorkflow = async (workflowId: number, workflowName: string) => {
    const confirmed = await confirm({
      title: `${t('common.delete')} ${workflowName}`,
      message: t('workflows.deleteWorkflowConfirm', 'Are you sure you want to delete this workflow? This action cannot be undone.'),
      variant: "danger",
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel'),
    });
    if (confirmed) {
      deleteWorkflow.mutate({ id: workflowId });
    }
  };

  // Handle delete stage with confirmation
  const handleDeleteStage = async (stageId: number, stageName: string) => {
    const confirmed = await confirm({
      title: `${t('common.delete')} ${stageName}`,
      message: t('workflows.deleteStageConfirm', 'Are you sure you want to delete this approval stage? This action cannot be undone.'),
      variant: "danger",
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel'),
    });
    if (confirmed) {
      deleteStage.mutate({ id: stageId });
    }
  };

  // Handle delete condition with confirmation
  const handleDeleteCondition = async (conditionId: number) => {
    const confirmed = await confirm({
      title: t('workflows.deleteCondition', 'Delete Condition'),
      message: t('workflows.deleteConditionConfirm', 'Are you sure you want to delete this condition? This action cannot be undone.'),
      variant: "danger",
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel'),
    });
    if (confirmed) {
      removeCondition.mutate({ id: conditionId });
    }
  };

  // Handle approer management
  const handleAddApprover = (stageId: number) => {
    setSelectedStageForApprovers(stageId);
    setEditingApprover(null);
    setIsApproverConfigOpen(true);
  };

  const handleEditApprover = (approver: any, stageId: number) => {
    setSelectedStageForApprovers(stageId);
    setEditingApprover(approver);
    setIsApproverConfigOpen(true);
  };

  const handleSaveApprover = (approverData: any) => {
    if (!selectedStageForApprovers) {
      toast.error(t('workflows.noStageSelected', 'No stage selected'));
      return;
    }

    // ApproverConfigModal sends { id, approvers: [...] }
    const { approvers } = approverData;
    
    if (!approvers || approvers.length === 0) {
      toast.error(t('workflows.noApproversToAdd', 'No approvers to add'));
      return;
    }

    if (editingApprover) {
      removeApprover.mutate({ id: editingApprover.id }, {
        onSuccess: () => {
          // Add all approvers from the payload
          approvers.forEach((approver: any) => {
            addApprover.mutate({
              stageId: selectedStageForApprovers,
              ...approver,
            });
          });
        }
      });
    } else {
      // Add all approvers from the payload
      approvers.forEach((approver: any) => {
        addApprover.mutate({
          stageId: selectedStageForApprovers,
          ...approver,
        });
      });
    }
    
    // Reset editing state
    setEditingApprover(null);
  };

  const handleDeleteApprover = async (approverId: number) => {
    const confirmed = await confirm({
      title: t('workflows.deleteApprover', 'Delete Approver'),
      message: t('workflows.deleteApproverConfirm', 'Are you sure you want to delete this approver? This action cannot be undone.'),
      variant: "danger",
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel'),
    });
    if (confirmed) {
      removeApprover.mutate({ id: approverId });
    }
  };

  // Handle escalation rules
  const handleOpenEscalationRules = (stageId: number) => {
    setSelectedStageForEscalation(stageId);
    setIsEscalationRulesOpen(true);
  };

  // Form state for creating workflow
  const [newWorkflow, setNewWorkflow] = useState({
    name: "",
    description: "",
    processType: "",
    priority: 0,
    isDefault: false,
  });

  // Form state for creating stage
  const [newStage, setNewStage] = useState({
    stageName: "",
    stageType: "role" as string,
    approvalMode: "any" as string,
    requiredApprovals: 1,
    approvalPercentage: 100,
    canReject: true,
    canRequestInfo: true,
    slaHours: 24,
    autoApproveOnSla: false,
    autoRejectOnSla: false,
    approverType: "role" as string,
    approverRoleId: "" as string,
    approverUserId: "" as string,
    approverGroupId: "" as string,
  });

  // Form state for creating condition
  const [newCondition, setNewCondition] = useState({
    conditionType: "",
    conditionOperator: "equals",
    conditionValue: "" as any,
    logicalGroup: 0,
  });

  // Filter workflows
  const filteredWorkflows = workflows?.filter((w) => {
    if (searchQuery && !w.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterProcessType && w.processType !== filterProcessType) {
      return false;
    }
    return true;
  });

  const handleCreateWorkflow = () => {
    if (!newWorkflow.name) {
      toast.error(t('workflows.workflowNameRequired', 'Workflow name is required'));
      return;
    }
    createWorkflow.mutate({
      name: newWorkflow.name,
      description: newWorkflow.description || undefined,
      processType: newWorkflow.processType as any || undefined,
      priority: newWorkflow.priority,
      isDefault: newWorkflow.isDefault,
    });
  };

  const handleEditStage = (stage: any) => {
    setEditingStage({
      id: stage.id,
      stageName: stage.stageName,
      stageType: stage.stageType || "role",
      approvalMode: stage.approvalMode || "any",
      requiredApprovals: stage.requiredApprovals || 1,
      approvalPercentage: stage.approvalPercentage || 100,
      canReject: stage.canReject ?? true,
      canRequestInfo: stage.canRequestInfo ?? true,
      slaHours: stage.slaHours || 24,
      autoApproveOnSla: stage.autoApproveOnSla ?? false,
      autoRejectOnSla: stage.autoRejectOnSla ?? false,
    });
    setIsEditStageDialogOpen(true);
  };

  const handleUpdateStage = () => {
    if (!editingStage || !editingStage.stageName) {
      toast.error(t('workflows.stageNameRequired', 'Stage name is required'));
      return;
    }
    updateStage.mutate({
      id: editingStage.id,
      stageName: editingStage.stageName,
      stageType: editingStage.stageType as any,
      approvalMode: editingStage.approvalMode as any,
      requiredApprovals: editingStage.requiredApprovals,
      approvalPercentage: editingStage.approvalPercentage,
      canReject: editingStage.canReject,
      canRequestInfo: editingStage.canRequestInfo,
      slaHours: editingStage.slaHours,
      autoApproveOnSla: editingStage.autoApproveOnSla,
      autoRejectOnSla: editingStage.autoRejectOnSla,
    });
  };

  const handleAddStage = () => {
    if (!selectedWorkflow || !newStage.stageName) {
      toast.error(t('workflows.stageNameRequired', 'Stage name is required'));
      return;
    }
    addStage.mutate({
      workflowId: selectedWorkflow,
      stageName: newStage.stageName,
      stageType: newStage.stageType as "individual" | "role" | "group" | "group_hierarchy" | "dynamic_field" | "manager" | "external_manager" | "site_manager" | "zone_owner" | "custom_resolver",
      approvalMode: newStage.approvalMode as "any" | "all" | "percentage",
      requiredApprovals: newStage.requiredApprovals,
      approvalPercentage: newStage.approvalPercentage,
      canReject: newStage.canReject,
      canRequestInfo: newStage.canRequestInfo,
      slaHours: newStage.slaHours,
      autoApproveOnSla: newStage.autoApproveOnSla,
      autoRejectOnSla: newStage.autoRejectOnSla,
      // Approver information
      approverType: newStage.approverType as "role" | "user" | "group" | "manager_chain",
      approverRoleId: newStage.approverRoleId ? parseInt(newStage.approverRoleId) : undefined,
      approverUserId: newStage.approverUserId ? parseInt(newStage.approverUserId) : undefined,
      approverGroupId: newStage.approverGroupId ? parseInt(newStage.approverGroupId) : undefined,
    });
  };

  const handleAddCondition = () => {
    if (!selectedWorkflow || !newCondition.conditionType) {
      toast.error(t('workflows.conditionTypeRequired', 'Condition type is required'));
      return;
    }
    if (!newCondition.conditionValue && !["is_null", "is_not_null"].includes(newCondition.conditionOperator)) {
      toast.error(t('workflows.conditionValueRequired', 'Condition value is required'));
      return;
    }
    
    // Format the value based on condition type
    let formattedValue = newCondition.conditionValue;
    
    // For numeric conditions, ensure value is a number
    if (["visitor_count", "request_duration", "access_level"].includes(newCondition.conditionType)) {
      formattedValue = Number(newCondition.conditionValue);
    }
    
    // For boolean conditions
    if (["has_mop", "has_mhv", "vip_visit", "escort_required"].includes(newCondition.conditionType)) {
      formattedValue = newCondition.conditionValue === "true";
    }

    addCondition.mutate({
      workflowId: selectedWorkflow,
      conditionType: newCondition.conditionType as any,
      conditionOperator: newCondition.conditionOperator as any,
      conditionValue: formattedValue,
      logicalGroup: newCondition.logicalGroup,
    });
  };

  // Get value options based on condition type
  const getValueOptions = (conditionType: string) => {
    switch (conditionType) {
      case "site_id":
        return sites?.map((s: { id: number; name: string }) => ({ value: String(s.id), label: s.name })) || [];
      case "zone_id":
        return zones?.map((z: { id: number; name: string }) => ({ value: String(z.id), label: z.name })) || [];
      case "area_id":
        return areas?.map((a: { id: number; name: string }) => ({ value: String(a.id), label: a.name })) || [];
      case "requester_group":
        return groups?.map((g: { id: number; name: string }) => ({ value: String(g.id), label: g.name })) || [];
      case "requester_department":
        return departments?.map((d: { id: number; name: string }) => ({ value: String(d.id), label: d.name })) || [];
      case "requester_role":
        return roles?.map((r: { id: number; name: string }) => ({ value: String(r.id), label: r.name })) || [];
      case "process_type":
        return PROCESS_TYPES;
      case "requester_type":
        return REQUESTER_TYPES;
      case "activity_risk":
        return RISK_LEVELS;
      case "day_of_week":
        return DAYS_OF_WEEK;
      case "access_level":
        return ACCESS_LEVELS;
      case "has_mop":
      case "has_mhv":
      case "vip_visit":
      case "escort_required":
        return [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ];
      default:
        return [];
    }
  };

  // Determine if condition type needs a dropdown or text input
  const isDropdownCondition = (conditionType: string) => {
    return [
      "site_id", "zone_id", "area_id", "requester_group", "requester_department",
      "requester_role", "process_type", "requester_type", "activity_risk",
      "day_of_week", "access_level", "has_mop", "has_mhv",
      "vip_visit", "escort_required"
    ].includes(conditionType);
  };

  // Get display value for condition
  const getConditionDisplayValue = (condition: any) => {
    const value = condition.conditionValue;
    const type = condition.conditionType;
    
    // Try to find a label for the value
    const options = getValueOptions(type);
    if (options.length > 0) {
      const option = options.find((o: { value: string; label: string }) => o.value === String(value));
      if (option) return option.label;
    }
    
    // For boolean values
    if (typeof value === "boolean") {
      return value ? t('common.yes', 'Yes') : t('common.no', 'No');
    }
    
    return JSON.stringify(value);
  };

  // Get condition type label
  const getConditionTypeLabel = (type: string) => {
    const conditionType = CONDITION_TYPES.find(ct => ct.value === type);
    return conditionType?.label || type;
  };

  // Get operator label
  const getOperatorLabel = (operator: string) => {
    const op = CONDITION_OPERATORS.find(o => o.value === operator);
    return op?.label || operator;
  };

  const getApproverSummary = (approver: any) => {
    if (approver.summary) return approver.summary;
    if (approver.approverReference) return approver.approverReference;
    return APPROVER_TYPES.find((type) => type.value === approver.approverType)?.label || approver.approverType;
  };

  // Group condition types by category
  const groupedConditionTypes = useMemo(() => {
    const groups: Record<string, typeof CONDITION_TYPES> = {};
    CONDITION_TYPES.forEach(ct => {
      if (!groups[ct.category]) {
        groups[ct.category] = [];
      }
      groups[ct.category].push(ct);
    });
    return groups;
  }, []);

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <ConfirmDialogComponent />
      <ErrorDialogComponent />
      <FioriPageHeader
        title={t('workflows.workflowBuilder', 'Workflow Builder')}
        subtitle={t('workflows.workflowBuilderSubtitle', 'Create and manage approval workflow rules')}
        icon={<Workflow className="h-5 w-5" />}
      />
      <div className="flex flex-col md:flex-row" style={{ height: 'calc(100vh - 160px)' }}>
      {/* Workflow List Panel — hidden on mobile when a workflow is selected */}
      <div className={`md:w-80 w-full border-r border-[#E0E0E0] bg-[#FAFAFA] flex flex-col ${selectedWorkflow ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-[#E0E0E0]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#2C2C2C] uppercase tracking-wider">{t('workflows.workflows', 'Workflows')}</h2>
            <Button size="sm" className="bg-[#5B2C93] hover:bg-[#3D1C5E] h-8" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6B]" />
            <Input
              placeholder={t('workflows.searchWorkflows', 'Search workflows...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filter */}
          <Select value={filterProcessType || "all"} onValueChange={(v) => setFilterProcessType(v === "all" ? null : v)}>
            <SelectTrigger>
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('workflows.allProcessTypes', 'All Process Types')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('workflows.allProcessTypes', 'All Process Types')}</SelectItem>
              {PROCESS_TYPES.map((pt) => (
                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Workflow List */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="text-center py-8 text-[#6B6B6B]">{t('common.loading', 'Loading...')}</div>
          ) : filteredWorkflows?.length === 0 ? (
            <div className="text-center py-8 text-[#6B6B6B]">
              <Workflow className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{t('workflows.noWorkflowsFound', 'No workflows found')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredWorkflows?.map((workflow) => (
                <Card
                  key={workflow.id}
                  className={`cursor-pointer transition-colors ${
                    selectedWorkflow === workflow.id
                      ? "border-primary bg-[#5B2C93]/5"
                      : "hover:bg-[#F5F5F5]/50"
                  }`}
                  onClick={() => setSelectedWorkflow(workflow.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Workflow className="h-4 w-4 text-[#5B2C93] shrink-0" />
                          <span className="font-medium truncate">{workflow.name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {workflow.processType && (
                            <Badge variant="secondary" className="text-xs">
                              {PROCESS_TYPES.find(pt => pt.value === workflow.processType)?.label || workflow.processType}
                            </Badge>
                          )}
                          {workflow.isDefault && (
                            <Badge variant="default" className="text-xs">{t('common.default', 'Default')}</Badge>
                          )}
                          {!workflow.isActive && (
                            <Badge variant="outline" className="text-xs text-[#D97706]">{t('common.inactive', 'Inactive')}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Workflow Details Panel */}
      {selectedWorkflow && workflowDetails ? (
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          {/* Mobile back button */}
          <button
            className="md:hidden flex items-center gap-1 text-sm text-[#5B2C93] font-medium mb-4"
            onClick={() => setSelectedWorkflow(null)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            {t('workflows.backToWorkflows', 'Back to Workflows')}
          </button>
          <div className="flex items-start justify-between mb-6">
            <div className="min-w-0 flex-1 pr-2">
              <h1 className="text-xl md:text-2xl font-medium text-[#2C2C2C] leading-8 truncate">{workflowDetails.workflow.name}</h1>
              <p className="text-[#6B6B6B] mt-1">
                {workflowDetails.workflow.description || "Default workflow for all access requests with L1 (Initial Review) and L2 (Security Approval) stages"}
              </p>
              <div className="flex items-center gap-2 mt-2">
                {workflowDetails.workflow.processType && (
                  <Badge variant="secondary">
                    {PROCESS_TYPES.find(pt => pt.value === workflowDetails.workflow.processType)?.label}
                  </Badge>
                )}
                <Badge variant="outline">{t('workflows.priority', 'Priority')}: {workflowDetails.workflow.priority}</Badge>
                {workflowDetails.workflow.isDefault && (
                  <Badge>{t('common.default', 'Default')}</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateWorkflow.mutate({
                  id: selectedWorkflow,
                  isActive: !workflowDetails.workflow.isActive,
                })}
              >
                {workflowDetails.workflow.isActive ? t('common.deactivate', 'Deactivate') : t('common.activate', 'Activate')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('common.duplicate', 'Duplicate')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-[#FF6B6B]"
                    onClick={() => handleDeleteWorkflow(selectedWorkflow, workflowDetails.workflow.name)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common.delete', 'Delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Tabs defaultValue="stages">
            <TabsList>
              <TabsTrigger value="stages">
                <GitBranch className="h-4 w-4 mr-2" />
                {t('workflows.stages', 'Stages')} ({workflowDetails.stages.length})
              </TabsTrigger>
              <TabsTrigger value="conditions">
                <Filter className="h-4 w-4 mr-2" />
                {t('workflows.conditions', 'Conditions')} ({workflowDetails.conditions.length})
              </TabsTrigger>
              <TabsTrigger value="simulator">
                <Settings className="h-4 w-4 mr-2" />
                {t('workflows.simulator', 'Simulator')}
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="h-4 w-4 mr-2" />
                {t('common.settings', 'Settings')}
              </TabsTrigger>
            </TabsList>

            {/* Stages Tab */}
            <TabsContent value="stages" className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base md:text-lg font-medium">{t('workflows.approvalStages', 'Approval Stages')}</h3>
                <Button size="sm" onClick={() => setIsStageDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('workflows.addStage', 'Add Stage')}
                </Button>
              </div>

              {workflowDetails.stages.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-[#6B6B6B]">
                    <GitBranch className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('workflows.noStagesConfigured', 'No stages configured.')}</p>
                    <p className="text-sm">{t('workflows.addStagesToDefineFlow', 'Add stages to define the approval flow.')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {workflowDetails.stages.map((stage, index) => (
                    <Card key={stage.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#5B2C93] text-white font-medium">
                              {index + 1}
                            </div>
                            <div>
                              <h4 className="font-medium">{stage.stageName}</h4>
                              <div className="flex items-center gap-2 mt-1 text-sm text-[#6B6B6B]">
                                <Badge variant="outline">
                                  {STAGE_TYPES.find(st => st.value === stage.stageType)?.label || stage.stageType}
                                </Badge>
                                <Badge variant="secondary">
                                  {APPROVAL_MODES.find(am => am.value === stage.approvalMode)?.label}
                                </Badge>
                                {stage.slaHours && (
                                  <Badge variant="secondary">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {stage.slaHours}h SLA
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-sm text-[#6B6B6B]">
                                {stage.canReject && (
                                  <span className="flex items-center gap-1">
                                    <XCircle className="h-3 w-3" /> {t('workflows.canReject', 'Can Reject')}
                                  </span>
                                )}
                                {stage.canRequestInfo && (
                                  <span className="flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" /> {t('workflows.canRequestInfo', 'Can Request Info')}
                                  </span>
                                )}
                                {(stage as any).approvers && (stage as any).approvers.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" /> {(stage as any).approvers.length} {(stage as any).approvers.length !== 1 ? t('workflows.approvers', 'Approvers') : t('workflows.approver', 'Approver')}
                                  </span>
                                )}
                              </div>
                              
                              {/* Approvers List */}
                              {(stage as any).approvers && (stage as any).approvers.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-[#6B6B6B] uppercase">{t('workflows.approvers', 'Approvers')}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleAddApprover(stage.id)}
                                      className="h-6 px-2 text-xs"
                                    >
                                      <Plus className="h-3 w-3 mr-1" />
                                      {t('common.add', 'Add')}
                                    </Button>
                                  </div>
                                  <div className="space-y-1">
                                    {(stage as any).approvers.map((approver: any) => (
                                      <div key={approver.id} className="flex items-center justify-between p-2 bg-[#F5F5F5] rounded text-xs">
                                        <div className="flex items-center gap-2">
                                          <UserCheck className="h-3 w-3 text-[#5B2C93]" />
                                          <span>
                                            {APPROVER_TYPES.find(at => at.value === approver.approverType)?.label || approver.approverType}
                                          </span>
                                          <span className="text-[#6B6B6B]">
                                            {getApproverSummary(approver)}
                                          </span>
                                          {approver.weight && approver.weight > 1 && (
                                            <Badge variant="secondary" className="text-xs px-1 py-0">
                                              w:{approver.weight}
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEditApprover(approver, stage.id)}
                                            className="h-5 w-5 p-0"
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteApprover(approver.id)}
                                            className="h-5 w-5 p-0"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Add Approver Button when no approvers */}
                              {(!(stage as any).approvers || (stage as any).approvers.length === 0) && (
                                <div className="mt-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleAddApprover(stage.id)}
                                    className="w-full"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    {t('workflows.addApprovers', 'Add Approvers')}
                                  </Button>
                                </div>
                              )}
                              
                              {/* Escalation Rules */}
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-[#6B6B6B] uppercase">{t('workflows.escalationRules', 'Escalation Rules')}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenEscalationRules(stage.id)}
                                    className="h-6 px-2 text-xs"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                      {t('common.configure', 'Configure')}
                                  </Button>
                                </div>
                                {((stage as any).escalationRules) && ((stage as any).escalationRules).length > 0 ? (
                                  <div className="space-y-1">
                                    {((stage as any).escalationRules).map((rule: any, index: number) => (
                                      <div key={rule.id || index} className="flex items-center gap-2 p-2 bg-[#F5F5F5] rounded text-xs">
                                        <AlertTriangle className="h-3 w-3 text-orange-500" />
                                        <span>{rule.trigger}</span>
                                        <span className="text-[#6B6B6B]">→</span>
                                        <span>{rule.action}</span>
                                        <Badge variant="secondary" className="text-xs px-1 py-0">
                                          {rule.afterHours}h
                                        </Badge>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-[#6B6B6B] italic">{t('workflows.noEscalationRules', 'No escalation rules configured')}</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditStage(stage)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteStage(stage.id, stage.stageName)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {index < workflowDetails.stages.length - 1 && (
                          <div className="flex justify-center mt-3">
                            <ChevronRight className="h-5 w-5 text-[#6B6B6B] rotate-90" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Conditions Tab */}
            <TabsContent value="conditions" className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base md:text-lg font-medium">{t('workflows.routingConditions', 'Routing Conditions')}</h3>
                <Button size="sm" onClick={() => setIsConditionDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('workflows.addCondition', 'Add Condition')}
                </Button>
              </div>

              {workflowDetails.conditions.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-[#6B6B6B]">
                    <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('workflows.noConditionsConfigured', 'No conditions configured.')}</p>
                    <p className="text-sm">{t('workflows.noConditionsDesc', 'This workflow will match all requests of its process type.')}</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('workflows.conditionType', 'Condition Type')}</TableHead>
                        <TableHead>{t('workflows.operator', 'Operator')}</TableHead>
                        <TableHead>{t('common.value', 'Value')}</TableHead>
                        <TableHead>{t('common.group', 'Group')}</TableHead>
                        <TableHead className="w-[100px]">{t('common.actions', 'Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {workflowDetails.conditions.map((condition) => (
                        <TableRow key={condition.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const ct = CONDITION_TYPES.find(c => c.value === condition.conditionType);
                                const Icon = ct?.icon || Filter;
                                return <Icon className="h-4 w-4 text-[#6B6B6B]" />;
                              })()}
                              {getConditionTypeLabel(condition.conditionType)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{getOperatorLabel(condition.conditionOperator)}</Badge>
                          </TableCell>
                          <TableCell>{getConditionDisplayValue(condition)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{t('common.group', 'Group')} {condition.logicalGroup}</Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCondition(condition.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}

              {workflowDetails.conditions.length > 0 && (
                <Card className="bg-[#F5F5F5]/50">
                  <CardContent className="py-4">
                    <p className="text-sm text-[#6B6B6B]">
                      <strong>{t('common.note', 'Note')}:</strong> {t('workflows.conditionsAndOrNote', 'Conditions within the same group are combined with AND logic. Different groups are combined with OR logic.')}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Simulator Tab */}
            <TabsContent value="simulator" className="space-y-4">
              <WorkflowSimulator 
                isOpen={true}
                onClose={() => {}}
              />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('workflows.workflowSettings', 'Workflow Settings')}</CardTitle>
                  <CardDescription>{t('workflows.workflowSettingsDesc', 'Configure general workflow behavior')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('workflows.priority', 'Priority')}</Label>
                      <Input
                        type="number"
                        value={workflowDetails.workflow.priority}
                        onChange={(e) => updateWorkflow.mutate({
                          id: selectedWorkflow,
                          priority: parseInt(e.target.value),
                        })}
                      />
                      <p className="text-xs text-[#6B6B6B]">
                        {t('workflows.priorityHint', 'Higher priority workflows are evaluated first')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('workflows.defaultWorkflow', 'Default Workflow')}</Label>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={workflowDetails.workflow.isDefault}
                          onCheckedChange={(checked) => updateWorkflow.mutate({
                            id: selectedWorkflow,
                            isDefault: checked,
                          })}
                        />
                        <span className="text-sm text-[#6B6B6B]">
                          {t('workflows.useAsDefaultHint', 'Use as default for this process type')}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-[#6B6B6B]">
          <div className="text-center">
            <Workflow className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium text-[#2C2C2C]">{t('workflows.selectWorkflow', 'Select a workflow to view details')}</p>
            <p className="text-sm mt-1">{t('workflows.orCreateNew', 'or create a new one to get started')}</p>
          </div>
        </div>
      )}
    </div>

    {/* Create Workflow Dialog */}
    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('workflows.createNewWorkflow', 'Create New Workflow')}</DialogTitle>
          <DialogDescription>
            {t('workflows.createWorkflowDesc', 'Define a new approval workflow for processing requests')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('workflows.workflowName', 'Workflow Name')} *</Label>
            <Input
              placeholder={t('workflows.workflowNamePlaceholder', 'e.g., Standard Admin Visit Approval')}
              value={newWorkflow.name}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('common.description', 'Description')}</Label>
            <Textarea
              placeholder={t('workflows.workflowDescPlaceholder', 'Describe the purpose of this workflow...')}
              value={newWorkflow.description}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('workflows.processType', 'Process Type')}</Label>
            <Select
              value={newWorkflow.processType}
              onValueChange={(v) => setNewWorkflow({ ...newWorkflow, processType: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('workflows.selectProcessType', 'Select process type')} />
              </SelectTrigger>
              <SelectContent>
                {PROCESS_TYPES.map((pt) => (
                  <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('workflows.priority', 'Priority')}</Label>
              <Input
                type="number"
                value={newWorkflow.priority}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, priority: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('workflows.defaultWorkflow', 'Default Workflow')}</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  checked={newWorkflow.isDefault}
                  onCheckedChange={(checked) => setNewWorkflow({ ...newWorkflow, isDefault: checked })}
                />
                <span className="text-sm">{t('workflows.setAsDefault', 'Set as default')}</span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCreateWorkflow} disabled={createWorkflow.isPending}>
            {createWorkflow.isPending ? t('common.creating', 'Creating...') : t('workflows.createWorkflow', 'Create Workflow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Stage Dialog */}
    <Dialog open={isStageDialogOpen} onOpenChange={setIsStageDialogOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('workflows.addApprovalStage', 'Add Approval Stage')}</DialogTitle>
          <DialogDescription>
            {t('workflows.addStageDesc', 'Configure a new stage in the approval workflow')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('workflows.stageName', 'Stage Name')} *</Label>
            <Input
              placeholder={t('workflows.stageNamePlaceholder', 'e.g., Manager Approval')}
              value={newStage.stageName}
              onChange={(e) => setNewStage({ ...newStage, stageName: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('workflows.stageType', 'Stage Type')} *</Label>
            <Select
              value={newStage.stageType}
              onValueChange={(v) => setNewStage({ ...newStage, stageType: v as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGE_TYPES.map((st) => (
                  <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('workflows.approvalMode', 'Approval Mode')}</Label>
            <Select
              value={newStage.approvalMode}
              onValueChange={(v) => setNewStage({ ...newStage, approvalMode: v as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APPROVAL_MODES.map((am) => (
                  <SelectItem key={am.value} value={am.value}>{am.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('workflows.slaHours', 'SLA Hours')}</Label>
            <Input
              type="number"
              value={newStage.slaHours}
              onChange={(e) => setNewStage({ ...newStage, slaHours: parseInt(e.target.value) || 0 })}
            />
          </div>
          {newStage.approvalMode === "percentage" && (
            <div className="space-y-2">
              <Label>{t('workflows.requiredPercentage', 'Required Percentage')}</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={newStage.approvalPercentage}
                onChange={(e) => setNewStage({ ...newStage, approvalPercentage: parseInt(e.target.value) || 100 })}
              />
            </div>
          )}
          {/* Approver Selection */}
          <div className="col-span-2 space-y-2">
            <Label>{t('workflows.approverType', 'Approver Type')} *</Label>
            <Select
              value={newStage.approverType}
              onValueChange={(v) => setNewStage({ ...newStage, approverType: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="role">{t('workflows.byRole', 'By Role')}</SelectItem>
                <SelectItem value="user">{t('workflows.specificUser', 'Specific User')}</SelectItem>
                <SelectItem value="group">{t('workflows.userGroup', 'User Group')}</SelectItem>
                <SelectItem value="manager_chain">{t('workflows.requesterManager', "Requester's Manager")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {newStage.approverType === "role" && (
            <div className="space-y-2">
              <Label>{t('workflows.selectRole', 'Select Role')}</Label>
              <Select
                value={newStage.approverRoleId}
                onValueChange={(v) => setNewStage({ ...newStage, approverRoleId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseRole', 'Choose a role...')} />
                </SelectTrigger>
                <SelectContent>
                  {roles?.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {newStage.approverType === "user" && (
            <div className="space-y-2">
              <Label>{t('workflows.selectUser', 'Select User')}</Label>
              <Select
                value={newStage.approverUserId}
                onValueChange={(v) => setNewStage({ ...newStage, approverUserId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseUser', 'Choose a user...')} />
                </SelectTrigger>
                <SelectContent>
                  {users?.users?.map((user: any) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {newStage.approverType === "group" && (
            <div className="space-y-2">
              <Label>{t('workflows.selectGroup', 'Select Group')}</Label>
            <Select
              value={newStage.approverGroupId}
                onValueChange={(v) => setNewStage({ ...newStage, approverGroupId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseGroup', 'Choose a group...')} />
                </SelectTrigger>
                <SelectContent>
                  {groups?.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {newStage.stageType === "approval_role" && (
            <div className="space-y-2">
              <Label>{t('workflows.selectApprovalRole', 'Select Approval Role')} *</Label>
              <Select
                value={newStage.approverRoleId}
                onValueChange={(v) => setNewStage({ ...newStage, approverRoleId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseApprovalRole', 'Choose an approval role...')} />
                </SelectTrigger>
                <SelectContent>
                  {approvalRoles?.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {newStage.stageType === "group_role" && (
            <div className="space-y-2">
              <Label>{t('workflows.selectGroup', 'Select Group')} *</Label>
              <Select
                value={newStage.approverGroupId}
                onValueChange={(v) => setNewStage({ ...newStage, approverGroupId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseGroup', 'Choose a group...')} />
                </SelectTrigger>
                <SelectContent>
                  {groups?.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2 grid grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={newStage.canReject}
                onCheckedChange={(checked) => setNewStage({ ...newStage, canReject: checked })}
              />
              <Label>{t('workflows.canReject', 'Can Reject')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newStage.canRequestInfo}
                onCheckedChange={(checked) => setNewStage({ ...newStage, canRequestInfo: checked })}
              />
              <Label>{t('workflows.canRequestInfo', 'Can Request Info')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newStage.autoApproveOnSla}
                onCheckedChange={(checked) => setNewStage({ ...newStage, autoApproveOnSla: checked })}
              />
              <Label>{t('workflows.autoApproveOnSla', 'Auto-approve on SLA')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newStage.autoRejectOnSla}
                onCheckedChange={(checked) => setNewStage({ ...newStage, autoRejectOnSla: checked })}
              />
              <Label>{t('workflows.autoRejectOnSla', 'Auto-reject on SLA')}</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsStageDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAddStage} disabled={addStage.isPending}>
            {addStage.isPending ? t('common.adding', 'Adding...') : t('workflows.addStage', 'Add Stage')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit Stage Dialog */}
    <Dialog open={isEditStageDialogOpen} onOpenChange={setIsEditStageDialogOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('workflows.editApprovalStage', 'Edit Approval Stage')}</DialogTitle>
          <DialogDescription>
            {t('workflows.editStageDesc', 'Modify the settings for this approval stage')}
          </DialogDescription>
        </DialogHeader>
        {editingStage && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
            <Label>{t('workflows.stageName', 'Stage Name')} *</Label>
            <Input
              placeholder={t('workflows.stageNamePlaceholder', 'e.g., Manager Approval')}
              value={editingStage.stageName}
                onChange={(e) => setEditingStage({ ...editingStage, stageName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
            <Label>{t('workflows.stageType', 'Stage Type')} *</Label>
            <Select
              value={editingStage.stageType}
                onValueChange={(v) => setEditingStage({ ...editingStage, stageType: v as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_TYPES.map((st) => (
                    <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
            <Label>{t('workflows.approvalMode', 'Approval Mode')}</Label>
            <Select
              value={editingStage.approvalMode}
                onValueChange={(v) => setEditingStage({ ...editingStage, approvalMode: v as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPROVAL_MODES.map((am) => (
                    <SelectItem key={am.value} value={am.value}>{am.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
            <Label>{t('workflows.slaHours', 'SLA Hours')}</Label>
            <Input
              type="number"
              value={editingStage.slaHours}
                onChange={(e) => setEditingStage({ ...editingStage, slaHours: parseInt(e.target.value) || 0 })}
              />
            </div>
            {editingStage.approvalMode === "percentage" && (
              <div className="space-y-2">
              <Label>{t('workflows.requiredPercentage', 'Required Percentage')}</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={editingStage.approvalPercentage}
                  onChange={(e) => setEditingStage({ ...editingStage, approvalPercentage: parseInt(e.target.value) || 100 })}
                />
              </div>
            )}
            {editingStage.stageType === "approval_role" && (
              <div className="space-y-2">
              <Label>{t('workflows.selectApprovalRole', 'Select Approval Role')} *</Label>
              <Select
                value={editingStage.approverRoleId}
                  onValueChange={(v) => setEditingStage({ ...editingStage, approverRoleId: v })}
                >
                  <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseApprovalRole', 'Choose an approval role...')} />
                </SelectTrigger>
                <SelectContent>
                  {approvalRoles?.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {editingStage.stageType === "group_role" && (
              <div className="space-y-2">
              <Label>{t('workflows.selectGroup', 'Select Group')} *</Label>
              <Select
                value={editingStage.approverGroupId}
                  onValueChange={(v) => setEditingStage({ ...editingStage, approverGroupId: v })}
                >
                  <SelectTrigger>
                  <SelectValue placeholder={t('workflows.chooseGroup', 'Choose a group...')} />
                </SelectTrigger>
                <SelectContent>
                  {groups?.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2 grid grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={editingStage.canReject}
                  onCheckedChange={(checked) => setEditingStage({ ...editingStage, canReject: checked })}
              />
              <Label>{t('workflows.canReject', 'Can Reject')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={editingStage.canRequestInfo}
                  onCheckedChange={(checked) => setEditingStage({ ...editingStage, canRequestInfo: checked })}
              />
              <Label>{t('workflows.canRequestInfo', 'Can Request Info')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={editingStage.autoApproveOnSla}
                  onCheckedChange={(checked) => setEditingStage({ ...editingStage, autoApproveOnSla: checked })}
              />
              <Label>{t('workflows.autoApproveOnSla', 'Auto-approve on SLA')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={editingStage.autoRejectOnSla}
                onCheckedChange={(checked) => setEditingStage({ ...editingStage, autoRejectOnSla: checked })}
              />
              <Label>{t('workflows.autoRejectOnSla', 'Auto-reject on SLA')}</Label>
            </div>
          </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setIsEditStageDialogOpen(false); setEditingStage(null); }}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleUpdateStage} disabled={updateStage.isPending}>
            {updateStage.isPending ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Condition Dialog */}
    <Dialog open={isConditionDialogOpen} onOpenChange={setIsConditionDialogOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('workflows.addRoutingCondition', 'Add Routing Condition')}</DialogTitle>
          <DialogDescription>
            {t('workflows.addConditionDesc', 'Define when this workflow should be applied to a request')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Condition Type Selection */}
          <div className="space-y-2">
            <Label>{t('workflows.conditionType', 'Condition Type')} *</Label>
            <Select
              value={newCondition.conditionType}
              onValueChange={(v) => setNewCondition({ 
                ...newCondition, 
                conditionType: v,
                conditionValue: "", // Reset value when type changes
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('workflows.selectConditionType', 'Select condition type')} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(groupedConditionTypes).map(([category, types]) => (
                  <div key={category}>
                    <div className="px-2 py-1.5 text-xs font-medium text-[#6B6B6B] bg-[#F5F5F5]">
                      {category}
                    </div>
                    {types.map((ct) => {
                      const Icon = ct.icon;
                      return (
                        <SelectItem key={ct.value} value={ct.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {ct.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operator Selection */}
          <div className="space-y-2">
            <Label>{t('workflows.operator', 'Operator')} *</Label>
            <Select
              value={newCondition.conditionOperator}
              onValueChange={(v) => setNewCondition({ ...newCondition, conditionOperator: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_OPERATORS.map((op) => (
                  <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value Selection - Dynamic based on condition type */}
          {newCondition.conditionType && !["is_null", "is_not_null"].includes(newCondition.conditionOperator) && (
            <div className="space-y-2">
              <Label>{t('common.value', 'Value')} *</Label>
              {isDropdownCondition(newCondition.conditionType) ? (
                <Select
                  value={String(newCondition.conditionValue)}
                  onValueChange={(v) => setNewCondition({ ...newCondition, conditionValue: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('common.selectValue', 'Select value')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getValueOptions(newCondition.conditionType).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={["visitor_count", "request_duration"].includes(newCondition.conditionType) ? "number" : "text"}
                  placeholder={
                    newCondition.conditionType === "visitor_count" ? "e.g., 5" :
                    newCondition.conditionType === "request_duration" ? "e.g., 24 (hours)" :
                    newCondition.conditionType === "time_range" ? "e.g., 09:00-17:00" :
                    "Enter value"
                  }
                  value={newCondition.conditionValue}
                  onChange={(e) => setNewCondition({ ...newCondition, conditionValue: e.target.value })}
                />
              )}
            </div>
          )}

          {/* Logical Group */}
          <div className="space-y-2">
            <Label>{t('workflows.logicalGroup', 'Logical Group')}</Label>
            <Select
              value={String(newCondition.logicalGroup)}
              onValueChange={(v) => setNewCondition({ ...newCondition, logicalGroup: parseInt(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{t('common.group', 'Group')} 0 ({t('common.default', 'Default')})</SelectItem>
                <SelectItem value="1">{t('common.group', 'Group')} 1</SelectItem>
                <SelectItem value="2">{t('common.group', 'Group')} 2</SelectItem>
                <SelectItem value="3">{t('common.group', 'Group')} 3</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[#6B6B6B]">
              {t('workflows.conditionsAndOrNote', 'Conditions within the same group are combined with AND logic. Different groups are combined with OR logic.')}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsConditionDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAddCondition} disabled={addCondition.isPending}>
            {addCondition.isPending ? t('common.adding', 'Adding...') : t('workflows.addCondition', 'Add Condition')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Approver Configuration Modal */}
    <ApproverConfigModal
      isOpen={isApproverConfigOpen}
      onClose={() => {
        setIsApproverConfigOpen(false);
        setEditingApprover(null);
        setSelectedStageForApprovers(null);
      }}
      onSave={handleSaveApprover}
      stageId={selectedStageForApprovers || undefined}
      editingApprover={editingApprover}
    />

    {/* Escalation Rules Modal */}
    <EscalationRulesModal
      isOpen={isEscalationRulesOpen}
      onClose={() => {
        setIsEscalationRulesOpen(false);
        setSelectedStageForEscalation(null);
      }}
      onRulesChanged={() => {
        refetchDetails();
      }}
      stageId={selectedStageForEscalation!}
      existingRules={selectedStageForEscalation ? 
        (workflowDetails?.stages.find(s => s.id === selectedStageForEscalation) as any)?.escalationRules || [] 
        : []
      }
    />
    </div>
  );
}
